/**
 * Componente botão para inserção de emojis em textareas
 * Utiliza o EmojiGrid centralizado e insere na posição do cursor
 */

import { useState } from 'react';
import { Smile } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { EmojiGrid } from '@/components/ui/emoji-grid';

interface EmojiInsertButtonProps {
  /** Referência ao textarea onde o emoji será inserido */
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  /** Callback chamado com o novo valor após inserção */
  onInsert: (newValue: string) => void;
}

export function EmojiInsertButton({ textareaRef, onInsert }: EmojiInsertButtonProps) {
  const [open, setOpen] = useState(false);

  /**
   * Insere emoji na posição atual do cursor no textarea
   */
  const handleEmojiSelect = (emoji: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentValue = textarea.value;
    
    // Insere emoji na posição do cursor
    const newValue = currentValue.substring(0, start) + emoji + currentValue.substring(end);
    onInsert(newValue);

    // Fecha o dialog
    setOpen(false);

    // Reposiciona cursor após inserção
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + emoji.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          type="button"
          variant="ghost" 
          size="icon" 
          className="h-8 w-8"
          title="Inserir emoji"
        >
          <Smile className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-[480px] p-0 gap-0">
        <DialogHeader className="px-4 pt-4 pb-2">
          <DialogTitle className="text-base">Inserir Emoji</DialogTitle>
        </DialogHeader>
        <EmojiGrid 
          onSelect={handleEmojiSelect}
          height="320px"
          columns={8}
          showSearch={true}
          showCategories={true}
        />
      </DialogContent>
    </Dialog>
  );
}
